import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Base URL for static files (photos, uploads) — strips the /api suffix
// e.g. https://api.reinlandsilage.com/api → https://api.reinlandsilage.com
export const BACKEND_BASE_URL = API_BASE_URL.endsWith('/api')
    ? API_BASE_URL.slice(0, -4)
    : API_BASE_URL;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('shift');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    logout: (endShift) => api.post('/auth/logout', { endShift }),
    getSession: () => api.get('/auth/session')
};

// Film Types API
export const filmTypesAPI = {
    getAll: () => api.get('/film-types'),
    create: (data) => api.post('/film-types', data),
    update: (id, data) => api.put(`/film-types/${id}`, data),
    delete: (id) => api.delete(`/film-types/${id}`)
};

// Shifts API
export const shiftsAPI = {
    getCurrent: () => api.get('/shifts/current'),
    end: () => api.post('/shifts/end'),
    getActive: () => api.get('/shifts/active')
};

// Box Entries API
export const boxEntriesAPI = {
    create: (data) => api.post('/box-entries', data),
    getAll: (params) => api.get('/box-entries', { params }),
    getLastBoxNumber: (filmTypeId) => api.get(`/box-entries/last-number/${filmTypeId}`),
    getByShift: (shiftId) => api.get(`/box-entries/shift/${shiftId}`),
    update: (id, data) => api.put(`/box-entries/${id}`, data),
    delete: (id) => api.delete(`/box-entries/${id}`)
};

// Admin API
export const adminAPI = {
    getDashboard: () => api.get('/admin/dashboard'),
    exportCSV: (params) => api.get('/admin/export/csv', {
        params,
        responseType: 'blob'
    }),
    getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
    getUsers: () => api.get('/admin/users'),
    createUser: (data) => api.post('/admin/users', data),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    toggleUserActive: (id) => api.patch(`/admin/users/${id}/toggle-active`),

    // Shift export tracking
    getShiftExportsPending: () => api.get('/admin/shift-exports/pending'),
    getShiftExportsAll: () => api.get('/admin/shift-exports/all'),
    getShiftExportsActive: () => api.get('/admin/shift-exports/active'),
    recordShiftExport: (shift_id, film_type_id) =>
        api.post('/admin/shift-exports', { shift_id, film_type_id }),

    // Film-type-centric export (new)
    getFilmTypeExportsPending: () => api.get('/admin/film-type-exports/pending'),
    markFilmTypeExported: (film_type_id, startDate, endDate) =>
        api.post('/admin/shift-exports/mark-film-type', { film_type_id, startDate, endDate }),
};

// Time API
export const timeAPI = {
    getServerTime: () => api.get('/time')
};

// Employees API
export const employeesAPI = {
    getActive: () => api.get('/employees'),
    getAll: () => api.get('/employees/all'),
    create: (formData) => api.post('/employees', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    update: (id, formData) => api.put(`/employees/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
};

// Attendance API
export const attendanceAPI = {
    checkIn: (shift_id, employee_ids) => api.post('/attendance/check-in', { shift_id, employee_ids }),
    getByShift: (shift_id) => api.get(`/attendance/shift/${shift_id}`),
    getReport: (params) => api.get('/attendance/report', { params })
};

// HR API
export const hrAPI = {
    // Phase 1: Employees
    getGroups: () => api.get('/hr/groups'),
    createGroup: (data) => api.post('/hr/groups', data),
    updateGroup: (id, data) => api.put(`/hr/groups/${id}`, data),
    deleteGroup: (id) => api.delete(`/hr/groups/${id}`),
    getEmployees: () => api.get('/hr/employees'),
    createEmployee: (formData) => api.post('/hr/employees', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updateEmployee: (id, formData) => api.put(`/hr/employees/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    toggleEmployeeStatus: (id, is_active) => api.patch(`/hr/employees/${id}/status`, { is_active }),
    
    // Phase 2: Attendance
    getDailyAttendance: (date) => api.get('/hr/attendance/daily', { params: { date } }),
    saveDailyAttendance: (date, records) => api.post('/hr/attendance/daily', { date, records }),
    saveBatchAttendance: (employeeId, records) => api.post('/hr/attendance/batch', { employeeId, records }),
    deleteAttendance: (employeeId, date) => api.delete('/hr/attendance', { params: { employeeId, date } }),
    getAttendanceCalendar: (employeeId, month) => api.get(`/hr/attendance/calendar/${employeeId}`, { params: { month } }),
    getAttendanceMatrix: (month) => api.get('/hr/attendance/matrix', { params: { month } }),
    getOTRecords: (params) => api.get('/hr/attendance/ot', { params }),
    toggleOT: (attendance_id, ot_day) => api.patch('/hr/attendance/ot', { attendance_id, ot_day }),
    toggleOTBulk: (attendance_ids, ot_day) => api.patch('/hr/attendance/ot/bulk', { attendance_ids, ot_day }),
    getAttendanceReport: (params) => api.get('/hr/attendance/report', { params }),

    // Phase 3: Advances
    getAdvances: (params) => api.get('/hr/advances', { params }),
    getAdvanceEligibility: (employeeId, date) => api.get(`/hr/advances/eligibility/${employeeId}`, { params: { date } }),
    giveAdvance: (data) => api.post('/hr/advances', data),
    giveBulkAdvance: (records) => api.post('/hr/advances/bulk', { records }),
    updateAdvance: (id, data) => api.put(`/hr/advances/${id}`, data),
    deleteAdvance: (id) => api.delete(`/hr/advances/${id}`),

    // Holidays
    getHolidays: (params) => api.get('/hr/holidays', { params }),
    addHoliday: (data) => api.post('/hr/holidays', data),
    deleteHoliday: (id) => api.delete(`/hr/holidays/${id}`),
    getHolidaySettings: () => api.get('/hr/holidays/settings'),
    updateHolidaySettings: (data) => api.patch('/hr/holidays/settings', data),

    // Phase 4: Salary engine
    getSalaryMonths: () => api.get('/hr/salary/months'),
    getEmployeeSalaryHistory: (employeeId) => api.get(`/hr/salary/employee/${employeeId}`),
    getSalaryMonth: (month) => api.get(`/hr/salary/${month}`),
    calculateSalary: (salary_month) => api.post('/hr/salary/calculate', { salary_month }),
    saveSalary: (salary_month, records) => api.post('/hr/salary/save', { salary_month, records }),
    finalizeSalary: (salary_month) => api.post('/hr/salary/finalize', { salary_month }),
    reopenSalary: (salary_month) => api.post('/hr/salary/reopen', { salary_month }),
    confirmEmployee: (salary_month, employee_id, record) => api.post('/hr/salary/confirm-employee', { salary_month, employee_id, record }),
    unconfirmEmployee: (salary_month, employee_id) => api.post('/hr/salary/unconfirm-employee', { salary_month, employee_id }),

    // Phase 5: Salary Slips & Settings
    getCompanySettings: () => api.get('/hr/settings/company'),
    updateCompanySettings: (data) => api.patch('/hr/settings/company', data),
    uploadCompanySeal: (formData) => api.post('/hr/settings/company/seal', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    deleteCompanySeal: () => api.delete('/hr/settings/company/seal'),
    generateSalarySlips: (month) => api.post(`/hr/salary/slips/generate/${month}`),
    getSalarySlipsList: (month) => api.get(`/hr/salary/slips/list/${month}`),
    // Bulk download returns a blob
    downloadBulkSlips: (month) => api.get(`/hr/salary/slips/${month}/bulk`, { responseType: 'blob' }),
    downloadSlip: (month, employeeId) => api.get(`/hr/salary/slips/${month}/${employeeId}`, { responseType: 'blob' })
};


// Printer API
export const printerAPI = {
    getSettings: () => api.get('/printer/settings'),
    updateSettings: (data) => api.put('/printer/settings', data),
    getPrinters: () => api.get('/printer/list'),
    testPrint: () => api.post('/printer/test-print'),
    reprint: (boxId, options = {}) => api.post(`/printer/reprint/${boxId}`, options)
};

// Inventory API (raw material / ledger expansion)
export const inventoryAPI = {
    getItems: (params) => api.get('/inventory/items', { params }),
    createItem: (data) => api.post('/inventory/items', data),
    updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
    deleteItem: (id) => api.delete(`/inventory/items/${id}`),
    deleteItemHard: (id) => api.delete(`/inventory/items/${id}`, { params: { hard: 1 } }),
    getStock: (params) => api.get('/inventory/stock', { params }),
    getLedger: (params) => api.get('/inventory/ledger', { params }),
    inward: (data) => api.post('/inventory/inward', data),
    // taxonomy
    getCategories: (params) => api.get('/inventory/categories', { params }),
    createCategory: (data) => api.post('/inventory/categories', data),
    updateCategory: (id, data) => api.put(`/inventory/categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/inventory/categories/${id}`),
    deleteCategoryHard: (id) => api.delete(`/inventory/categories/${id}`, { params: { hard: 1 } }),
    getCategoryUsage: (id) => api.get(`/inventory/categories/${id}/usage`),
    previewItemCode: (categoryId) => api.get('/inventory/items/code-preview', { params: { categoryId } }),
    // BOM
    getBom: (filmTypeId) => api.get(`/inventory/bom/${filmTypeId}`),
    saveBom: (data) => api.post('/inventory/bom', data),
    deleteBom: (filmTypeId) => api.delete(`/inventory/bom/${filmTypeId}`),
    // Physical audit
    auditAdjust: (data) => api.post('/inventory/audit-adjust', data),
    getNegativeStock: () => api.get('/inventory/stock', { params: { negative: 1 } }),
    // Operator: granule checks, ad-hoc store issue, end-of-shift scrap
    granuleCheck: (data) => api.post('/inventory/granule', data),
    getGranuleRecent: (params) => api.get('/inventory/granule/recent', { params }),
    storeIssue: (data) => api.post('/inventory/store-issue', data),
    recordScrap: (data) => api.post('/inventory/scrap', data),
    rewind: (data) => api.post('/inventory/rewind', data),
    jumboProduction: (data) => api.post('/inventory/jumbo-production', data),
    getJumboRecent: (params) => api.get('/inventory/jumbo-production/recent', { params }),
    // Reporting (Phase 6) — admin only
    getReportData: (params) => api.get('/inventory/reports/data', { params }),
    downloadReportPdf: (params) => api.get('/inventory/reports/production.pdf', { params, responseType: 'blob' }),
    downloadReportCsv: (params) => api.get('/inventory/reports/export.csv', { params, responseType: 'blob' }),
};

export default api;
